import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
  Skeleton,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useState } from 'react';

/**
 * Status page data structure for the row
 */
export interface StatusPageRowData {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  isPublic: boolean;
  monitorCount: number;
}

/**
 * StatusPageRow component props
 */
interface StatusPageRowProps {
  statusPage: StatusPageRowData;
  onDelete?: (id: string) => void;
  isLoading?: boolean;
}

/**
 * StatusPageRow loading skeleton
 */
export function StatusPageRowSkeleton() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        py: 1.25,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Skeleton variant="circular" width={10} height={10} />
      <Skeleton variant="text" width={150} height={24} />
      <Skeleton
        variant="rounded"
        width={100}
        height={24}
        sx={{ display: { xs: 'none', sm: 'block' } }}
      />
      <Box sx={{ flex: 1 }} />
      <Skeleton variant="text" width={70} height={20} />
      <Skeleton variant="circular" width={28} height={28} />
    </Box>
  );
}

/**
 * StatusPageRow component
 * Displays status page information in a compact row format
 */
export function StatusPageRow({ statusPage, onDelete, isLoading = false }: StatusPageRowProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = () => {
    handleMenuClose();
    onDelete?.(statusPage.id);
  };

  if (isLoading) {
    return <StatusPageRowSkeleton />;
  }

  const publicUrl = `${window.location.origin}/app/status/${statusPage.slug}`;

  return (
    <>
      <Box
        component={RouterLink}
        to={`/status-pages/${statusPage.id}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1.5, sm: 2 },
          px: 2,
          py: 1.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
          textDecoration: 'none',
          color: 'inherit',
          transition: 'background-color 0.15s',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        {/* Visibility indicator */}
        <Tooltip title={statusPage.isPublic ? 'Public' : 'Private'}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: statusPage.isPublic ? '#10B981' : '#6B7280',
              flexShrink: 0,
            }}
          />
        </Tooltip>

        {/* Name and description */}
        <Box
          sx={{
            minWidth: { xs: 100, sm: 150 },
            maxWidth: { xs: 150, sm: 250 },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {statusPage.name}
          </Typography>
          {statusPage.description && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {statusPage.description}
            </Typography>
          )}
        </Box>

        {/* Public URL */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
          }}
        >
          {statusPage.isPublic ? (
            <Typography
              component="a"
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              onClick={(e) => e.stopPropagation()}
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              /status/{statusPage.slug}
            </Typography>
          ) : (
            <Typography
              variant="body2"
              color="text.disabled"
              sx={{ fontStyle: 'italic' }}
            >
              Private
            </Typography>
          )}
        </Box>

        {/* Monitor count */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            flexShrink: 0,
            minWidth: 80,
            textAlign: 'right',
          }}
        >
          {statusPage.monitorCount} monitor{statusPage.monitorCount !== 1 ? 's' : ''}
        </Typography>

        {/* Actions */}
        <IconButton
          size="small"
          onClick={handleMenuOpen}
          sx={{
            ml: 0.5,
            opacity: 0.5,
            '&:hover': { opacity: 1 },
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem
          component={RouterLink}
          to={`/status-pages/${statusPage.id}`}
          onClick={handleMenuClose}
        >
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>

        {statusPage.isPublic && (
          <MenuItem
            component="a"
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleMenuClose}
          >
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Open Public Page</ListItemText>
          </MenuItem>
        )}

        <MenuItem
          component={RouterLink}
          to={`/status-pages/${statusPage.id}/edit`}
          onClick={handleMenuClose}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}

export default StatusPageRow;
